import { AnimatePresence, motion } from 'framer-motion';
import { FC, useId, useMemo } from 'react';

import { Box, useColorModeValue, useToken } from '@bonusly/design-system';

import { BoredSVG, STATE_PARTS } from './states';
import { BizyAnimationOverrides, BizyProps, BizySize, BizyState, isPartAnimatedState, PartAnimatedState, WingFlutterMode } from './types';

/**
 * Bizy - The Bonusly bee mascot component
 *
 * A cute bee character that can display different emotional states.
 * Supports smooth part-based animations between states (antennae, wings, body, eyes, arms).
 * The bored state uses crossfade transition due to its unique rotated pose.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Bizy state="excited" />
 *
 * // With custom size
 * <Bizy state="thinking" size="lg" />
 *
 * // Numeric size
 * <Bizy state="curious" size={100} />
 * ```
 */
export const Bizy: FC<BizyProps> = ({ state = 'curious', size = 'md', freeze = false, className, animations }) => {
	const sizeValue = useMemo(() => getSizeValue(size), [size]);

	return (
		<Box className={className} display="inline-flex" alignItems="center" justifyContent="center">
			<AnimatedBizySVG state={state} width={sizeValue} height={sizeValue} freeze={freeze} animations={animations} />
		</Box>
	);
};

const TRANSITION = {
	duration: 0.3,
	ease: [0.4, 0, 0.2, 1] as const,
};

const SPRING_TRANSITION = {
	type: 'spring' as const,
	stiffness: 200,
	damping: 20,
};

const getSizeValue = (size: BizySize): number => {
	if (typeof size === 'number') return size;
	const sizeMap: Record<string, number> = { sm: 48, md: 80, lg: 120, xl: 200 };
	return sizeMap[size] || 80;
};

const curvedStripePath = (y: number, h: number) =>
	`M70 ${y + 3}C120 ${y - 2} 180 ${y - 2} 240 ${y + 3}L240 ${y + h + 3}C180 ${y + h - 2} 120 ${y + h - 2} 70 ${y + h + 3}Z`;

interface AnimatedBizyProps {
	state: BizyState;
	width: number;
	height: number;
	freeze?: boolean;
	className?: string;
	animations?: BizyAnimationOverrides;
}

const FROZEN = { rotate: 0, scale: 1, scaleX: 1, scaleY: 1 };

const buildWingFlutter = (amplitude: number, speed: number, mode: WingFlutterMode, offset = false) => {
	const transition = {
		duration: speed,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
		...(offset ? { delay: speed * 0.2 } : {}),
	};

	if (mode === 'scaleY') {
		const minScale = Math.max(1 - amplitude * 0.15, 0.1);
		return {
			scaleY: offset ? [1, minScale, 1, 1, 1] : [1, 1, 1, minScale, 1],
			transition,
		};
	}

	if (mode === 'squishStretch') {
		const minY = Math.max(1 - amplitude * 0.12, 0.2);
		const maxX = 1 + amplitude * 0.04;
		return {
			scaleY: offset ? [1, minY, 1, 1, 1] : [1, 1, 1, minY, 1],
			scaleX: offset ? [1, maxX, 1, 1, 1] : [1, 1, 1, maxX, 1],
			transition,
		};
	}

	// Default: rotate mode
	const scaleAmount = Math.min(amplitude * 0.01, 0.05);
	return {
		rotate: offset ? [0, amplitude, 0, -amplitude, 0] : [0, -amplitude, 0, amplitude, 0],
		scale: offset ? [1, 1 - scaleAmount, 1, 1 + scaleAmount, 1] : [1, 1 + scaleAmount, 1, 1 - scaleAmount, 1],
		transition,
	};
};

const HOVER_BOB = {
	y: [0, -5, 0, 3, 0],
	rotate: [-0.3, 0, 0.3, 0, -0.3],
	transition: {
		duration: 2.5,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
	},
};

const BLINK_ANIMATION = {
	scaleY: [1, 1, 1, 1, 0.1, 1, 1, 1],
	transition: {
		duration: 8,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
		times: [0, 0.775, 0.79, 0.795, 0.8, 0.805, 0.82, 1],
	},
};

const WAVE_ANIMATION = {
	rotate: [-8, 8, -8],
	transition: {
		duration: 0.5,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
	},
};

const ANTENNA_SWAY_LEFT = {
	rotate: [-0.8, 0.8, -0.8],
	transition: {
		duration: 3,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
	},
};

const ANTENNA_SWAY_RIGHT = {
	rotate: [0.6, -0.6, 0.6],
	transition: {
		duration: 3.4,
		ease: 'easeInOut' as const,
		repeat: Infinity,
		repeatType: 'loop' as const,
		delay: 0.3,
	},
};

const ArmPath: FC<{ arm: { d: string }; outlineColor: string; outlineOpacity: number }> = ({ arm, outlineColor, outlineOpacity }) => (
	<motion.path
		d={arm.d}
		stroke={outlineColor}
		opacity={outlineOpacity}
		strokeWidth="18.2362"
		strokeLinecap="round"
		fill="none"
		initial={false}
		animate={{ d: arm.d }}
		transition={TRANSITION}
	/>
);

const AnimatedBizySVG: FC<AnimatedBizyProps> = ({ state, width, height, freeze = false, className, animations }) => {
	const instanceId = useId();
	const animatedState: PartAnimatedState = isPartAnimatedState(state) ? state : 'curious';
	const parts = useMemo(() => STATE_PARTS[animatedState], [animatedState]);
	const clipId = `clip_bizy_${instanceId}`;

	const leftArm = parts.arms.find((a) => a.side === 'left');
	const rightArm = parts.arms.find((a) => a.side === 'right');
	const armOrder = parts.armOrder ?? 'rightFront';
	const isBored = state === 'bored';

	const behindArm = armOrder === 'leftFront' ? rightArm : armOrder === 'bothFront' ? null : leftArm;
	const frontArms = armOrder === 'bothFront' ? [leftArm, rightArm] : armOrder === 'leftFront' ? [leftArm] : [rightArm];

	const antennaOrder = parts.antennaOrder ?? 'rightFront';
	const behindAntenna = antennaOrder === 'leftFront' ? 'right' : antennaOrder === 'bothFront' ? null : 'left';

	const wingAmp = animations?.wingAmplitude ?? 0.2;
	const wingSpd = animations?.wingSpeed ?? 0.3;
	const wingMode = animations?.wingMode ?? 'squishStretch';
	const wingFlutter = useMemo(() => buildWingFlutter(wingAmp, wingSpd, wingMode), [wingAmp, wingSpd, wingMode]);
	const wingFlutterOffset = useMemo(() => buildWingFlutter(wingAmp, wingSpd, wingMode, true), [wingAmp, wingSpd, wingMode]);
	const wingNeedsBodyOrigin = wingMode !== 'rotate';

	const outlineColor = useColorModeValue('#051236', '#515661');
	const outlineOpacity = useColorModeValue(1, 0.8);
	const [gray100] = useToken('colors', ['gray.100']);
	const wingColor = useColorModeValue('#DBDFE6', gray100);
	const wingOpacity = useColorModeValue(1, 0.8);
	const wingBlendMode = useColorModeValue('multiply', 'normal') as 'multiply' | 'normal';

	const useHoverBob = (animations?.hoverBob ?? true) && !freeze;

	const antennaSway = (animations?.antennaSway ?? true) && !freeze;
	const antennaSwayLeft = antennaSway ? ANTENNA_SWAY_LEFT : { rotate: 0 };
	const antennaSwayRight = antennaSway ? ANTENNA_SWAY_RIGHT : { rotate: 0 };

	const renderAntenna = (side: 'left' | 'right') => (
		<motion.g
			key={`antenna-${side}`}
			style={{ originX: side === 'left' ? '155px' : '200px', originY: '60px' }}
			animate={side === 'left' ? antennaSwayLeft : antennaSwayRight}
		>
			<motion.path
				fill={outlineColor}
				opacity={outlineOpacity}
				initial={false}
				animate={{ d: side === 'left' ? parts.antenna1.path : parts.antenna2.path }}
				transition={SPRING_TRANSITION}
			/>
		</motion.g>
	);

	return (
		<AnimatePresence mode="wait">
			{!isBored ? (
				<motion.svg
					key="animated"
					className={className}
					width={width}
					height={height}
					viewBox="0 0 316 278"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.25 }}
				>
					<motion.g animate={useHoverBob ? HOVER_BOB : { y: 0, rotate: 0 }}>
						<motion.g animate={{ rotate: parts.bodyRotation ?? 0 }} transition={SPRING_TRANSITION} style={{ transformOrigin: '158px 143px' }}>
							{/* Arm behind body */}
							{behindArm && <ArmPath arm={behindArm} outlineColor={outlineColor} outlineOpacity={outlineOpacity} />}

							{/* Wings — key forces remount when speed/amplitude/mode change so the loop restarts */}
							<motion.g
								key={`w1-${wingSpd}-${wingAmp}-${wingMode}`}
								style={{ mixBlendMode: wingBlendMode, originX: wingNeedsBodyOrigin ? '155px' : '175px', originY: wingNeedsBodyOrigin ? '130px' : '120px' }}
								animate={freeze ? FROZEN : wingFlutter}
							>
								<motion.path
									d={parts.wing1Path}
									fill={wingColor}
									opacity={wingOpacity}
									initial={false}
									animate={{ d: parts.wing1Path }}
									transition={TRANSITION}
								/>
							</motion.g>
							<motion.g
								key={`w2-${wingSpd}-${wingAmp}-${wingMode}`}
								style={{ mixBlendMode: wingBlendMode, originX: wingNeedsBodyOrigin ? '155px' : '175px', originY: wingNeedsBodyOrigin ? '145px' : '140px' }}
								animate={freeze ? FROZEN : wingFlutterOffset}
							>
								<motion.path
									d={parts.wing2Path}
									fill={wingColor}
									opacity={wingOpacity}
									initial={false}
									animate={{ d: parts.wing2Path }}
									transition={TRANSITION}
								/>
							</motion.g>
							<motion.g
								key={`w3-${wingSpd}-${wingAmp}-${wingMode}`}
								style={{ mixBlendMode: wingBlendMode, originX: wingNeedsBodyOrigin ? '155px' : '130px', originY: wingNeedsBodyOrigin ? '130px' : '120px' }}
								animate={freeze ? FROZEN : wingFlutter}
							>
								<motion.path
									d={parts.wing3Path}
									fill={wingColor}
									opacity={wingOpacity}
									initial={false}
									animate={{ d: parts.wing3Path }}
									transition={TRANSITION}
								/>
							</motion.g>
							<motion.g
								key={`w4-${wingSpd}-${wingAmp}-${wingMode}`}
								style={{ mixBlendMode: wingBlendMode, originX: wingNeedsBodyOrigin ? '155px' : '130px', originY: wingNeedsBodyOrigin ? '145px' : '140px' }}
								animate={freeze ? FROZEN : wingFlutterOffset}
							>
								<motion.path
									d={parts.wing4Path}
									fill={wingColor}
									opacity={wingOpacity}
									initial={false}
									animate={{ d: parts.wing4Path }}
									transition={TRANSITION}
								/>
							</motion.g>

							{/* Antenna behind body (if any) */}
							{behindAntenna === 'left' && renderAntenna('left')}
							{behindAntenna === 'right' && renderAntenna('right')}

							{/* Body with clip path */}
							<defs>
								<clipPath id={clipId}>
									<motion.rect
										fill="white"
										initial={false}
										animate={{ x: parts.body.x, y: parts.body.y, width: parts.body.width, height: parts.body.height, rx: parts.body.rx }}
										transition={SPRING_TRANSITION}
									/>
								</clipPath>
							</defs>

							<g clipPath={`url(#${clipId})`}>
								{/* Main body */}
								<motion.rect
									fill="#E6AE14"
									initial={false}
									animate={{ x: parts.body.x, y: parts.body.y, width: parts.body.width, height: parts.body.height, rx: parts.body.rx }}
									transition={SPRING_TRANSITION}
								/>

								{/* Body highlight */}
								<motion.rect
									fill="#FFCC55"
									initial={false}
									animate={{
										x: parts.bodyHighlight.x,
										y: parts.bodyHighlight.y,
										width: parts.bodyHighlight.width,
										height: parts.bodyHighlight.height,
										rx: parts.bodyHighlight.rx,
									}}
									transition={SPRING_TRANSITION}
								/>

								{/* Stripes (curved upward in center to match SVG design) */}
								<g style={{ mixBlendMode: 'darken' }}>
									<motion.path
										fill="#051236"
										initial={false}
										animate={{ d: curvedStripePath(parts.stripes.y1, parts.stripes.height) }}
										transition={SPRING_TRANSITION}
									/>
								</g>
								<g style={{ mixBlendMode: 'darken' }}>
									<motion.path
										fill="#051236"
										initial={false}
										animate={{ d: curvedStripePath(parts.stripes.y2, parts.stripes.height) }}
										transition={SPRING_TRANSITION}
									/>
								</g>
								<g style={{ mixBlendMode: 'darken' }}>
									<motion.path
										fill="#051236"
										initial={false}
										animate={{ d: curvedStripePath(parts.stripes.y3, parts.stripes.height) }}
										transition={SPRING_TRANSITION}
									/>
								</g>

								{/* Left arm inside clip (for thinking state chin-rest pose) */}
								{animatedState === 'thinking' && leftArm && <ArmPath arm={leftArm} outlineColor={outlineColor} outlineOpacity={outlineOpacity} />}

								{/* Eyes inside body clip — clipped at body edge when sliding off during turns */}
								<motion.g
									style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
									animate={(animations?.blink ?? true) && !freeze ? BLINK_ANIMATION : { scaleY: 1 }}
								>
									<motion.path fill="#051236" initial={false} animate={{ d: parts.eyes.leftPath }} transition={SPRING_TRANSITION} />
									<motion.path fill="#051236" initial={false} animate={{ d: parts.eyes.rightPath }} transition={SPRING_TRANSITION} />
								</motion.g>

								{/* Badge inside body clip — slides off edge during turns */}
								<g>
									<motion.ellipse
										fill="#4D3505"
										initial={false}
										animate={{
											cx: parts.badge.outerEllipse.cx,
											cy: parts.badge.outerEllipse.cy,
											rx: parts.badge.outerEllipse.rx,
											ry: parts.badge.outerEllipse.ry,
										}}
										transition={SPRING_TRANSITION}
									/>
									<motion.ellipse
										fill="#B37E00"
										initial={false}
										animate={{
											cx: parts.badge.innerEllipse.cx,
											cy: parts.badge.innerEllipse.cy,
											rx: parts.badge.innerEllipse.rx,
											ry: parts.badge.innerEllipse.ry,
										}}
										transition={SPRING_TRANSITION}
									/>
									<motion.path
										fill="#DBDFE6"
										fillRule="evenodd"
										clipRule="evenodd"
										initial={false}
										animate={{ d: parts.badge.starPath }}
										transition={SPRING_TRANSITION}
									/>
									<motion.path fill="#F6F6F9" initial={false} animate={{ d: parts.badge.starHighlightPath }} transition={SPRING_TRANSITION} />
								</g>
							</g>

							{/* Antenna(e) in front of body */}
							{behindAntenna !== 'left' && renderAntenna('left')}
							{behindAntenna !== 'right' && renderAntenna('right')}

							{/* Stinger */}
							<motion.path fill={outlineColor} opacity={outlineOpacity} initial={false} animate={{ d: parts.stinger }} transition={TRANSITION} />

							{/* Arm(s) in front of body */}
							{frontArms.map((arm) => {
								if (!arm) return null;
								const isWavingArm = animatedState === 'waving' && arm.side === 'right';
								return isWavingArm ? (
									<motion.g key={arm.side} animate={freeze ? FROZEN : WAVE_ANIMATION} style={{ transformBox: 'view-box', transformOrigin: '201px 135px' }}>
										<ArmPath arm={arm} outlineColor={outlineColor} outlineOpacity={outlineOpacity} />
									</motion.g>
								) : (
									<ArmPath key={arm.side} arm={arm} outlineColor={outlineColor} outlineOpacity={outlineOpacity} />
								);
							})}

							{/* Stars (for excited state) */}
							{!freeze &&
								parts.stars?.map((starPath, index) => (
									<motion.path
										key={`star-${index}`}
										d={starPath}
										fill="#FCC84F"
										initial={{ opacity: 0, scale: 0 }}
										animate={{
											opacity: [0, 1, 0.7, 1, 0.8, 1],
											scale: [0, 1.2, 0.9, 1.1, 0.95, 1],
											rotate: [0, 0, -5, 5, -3, 0],
										}}
										transition={{
											duration: 1.5,
											delay: index * 0.15,
											repeat: Infinity,
											repeatDelay: 0.5,
											ease: 'easeInOut',
										}}
										style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
									/>
								))}
						</motion.g>
					</motion.g>
				</motion.svg>
			) : (
				<motion.div key="bored" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className={className}>
					<BoredSVG width={width} height={height} />
				</motion.div>
			)}
		</AnimatePresence>
	);
};
